import api from "./api";
import { PaginatedResponse } from "@/types/borrower";

/** Roles an org manager can assign; the system `admin` role is never one of them. */
export const MANAGED_USER_ROLES = ["broker", "underwriter", "admin_support"] as const;
export type ManagedUserRole = (typeof MANAGED_USER_ROLES)[number];

/** `userService.list` login filter: brokers with a Steddy login, and email-only (ghost) brokers. */
export const USER_LOGINS = ["registered", "email_only"] as const;
type UserLogin = (typeof USER_LOGINS)[number];

/** A row of a user page; everything is about the manager's organization (their default one). */
export interface ManagedUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  /** Display name; the email when there is no name, so tables use first and last instead. */
  name: string;
  role: ManagedUserRole;
  company_name: string;
  /** False for email-only brokers created from inbound mail: nothing to activate. */
  has_login: boolean;
  /** The user's status in the manager's organization; null for email-only brokers. */
  is_active: boolean | null;
  created_at: string;
  /** Also active in other organizations, so deactivating here keeps them signed in. */
  in_other_organizations: boolean;
  /** Why the company cannot be changed, or null. */
  account_lock: string | null;
  /** Why the user cannot be activated or deactivated, or null. */
  membership_lock: string | null;
  /** Email-only brokers nothing points at. */
  can_delete: boolean;
  /** Why an email-only broker can't be deleted, or null. */
  delete_blocked_reason: string | null;
}

export interface ManagedUserFilters {
  role: ManagedUserRole;
  search?: string;
  /** Empty means both. */
  logins?: UserLogin[];
  page?: number;
}

interface ManagedUserUpdate {
  company_name?: string;
  is_active?: boolean;
}

/** Per role: the unfiltered list size and its pending invitations. */
type UserCounts = Record<ManagedUserRole, { users: number; pending_invites: number }>;

/** Query params shared by the users and invitations lists. */
export const listParams = ({ role, search, page }: ManagedUserFilters) => ({
  role,
  search: search || undefined,
  page,
});

export const userService = {
  list: async (filters: ManagedUserFilters): Promise<PaginatedResponse<ManagedUser>> => {
    const response = await api.get<PaginatedResponse<ManagedUser>>("/accounts/users/", {
      params: {
        ...listParams(filters),
        login: filters.logins?.length ? filters.logins.join(",") : undefined,
      },
    });
    return response.data;
  },

  counts: async (): Promise<UserCounts> => {
    const response = await api.get<UserCounts>("/accounts/users/counts/");
    return response.data;
  },

  update: async ({ id, data }: { id: number; data: ManagedUserUpdate }): Promise<ManagedUser> => {
    const response = await api.patch<ManagedUser>(`/accounts/users/${id}/`, data);
    return response.data;
  },

  /** Email-only brokers only; the server refuses anyone else or one that is still referenced. */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/accounts/users/${id}/`);
  },
};
